import TableOnly from "components/DataTable/TableOnly";
import RefreshButton from "components/RefreshButton/RefreshButton";
import PaginationWithState from "components/Pagination/PaginationWithState";
import Spinner from "components/Spinner";
import ZplAddressLabelButton from "components/ZplLabel/ZplAddressLabelButton";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import UpsAdvanceSearchModal from "./AdvanceSearch/UpsAdvanceSearchModal";
import { collectVoidedTrackingNumbers } from "./voidedTrackingNumbers";
import { CourierCell, hasTrackingApi, trackingUrlFor } from "./courier";
import BulkUpdateStatus from "./BulkUpdateStatus";
import BulkUpdateFieldsModal from "./BulkUpdateFieldsModal";
import ShipmentEmailModal from "./ShipmentEmailModal";
import TrackingDetailsModal from "./TrackingDetailsModal";
import ManualEntryForm, {
  customerLabel,
  isDateOnlyRow,
  isDeletableRow,
  STATUS_OPTIONS,
} from "./ManualEntryForm";

// The row's Tracking details (i) button on the Shipments page: hidden (user,
// 2026-10-22: "hide tracking details icon button in shipments"). The
// customer's Shipments tab still shows it. true brings it back.
const SHOW_DETAILS_ON_PAGE = false;

// Round 3: status tabs. A row with no status is listed as In-Transit -- every
// row from before round 3 has none (nova-api listFilters).
// Unlinked (user, 2026-10-16): rows with no customer, whatever their status,
// so they can be linked by hand with the row's edit button.
// No Delivered tab (user, 2026-10-22). No counts on the tabs either (user,
// 2026-10-22, after trying them). Not Emailed (user, 2026-10-26): shipments
// whose tracking number was never emailed to the customer, voided ones left
// out (nova-api listFilters).
const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "in-transit", label: "In-Transit" },
  { value: "unlinked", label: "Unlinked" },
  { value: "not-emailed", label: "Not Emailed" },
];
// A customer's tab: no Unlinked (its rows are all linked), but Delivered
// stays (user, 2026-10-22).
const CUSTOMER_STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "in-transit", label: "In-Transit" },
  { value: "delivered", label: "Delivered" },
  { value: "not-emailed", label: "Not Emailed" },
];

// The WO CUSTOMER is the linked ERP customer (W-ID or set by hand). The
// COMPANY NAME is the shipment's own -- the name the tracker sent, stored in
// the customerName column (back since 2026-10-21). A label or a pasted slip
// goes to the company on the parcel, so that comes first.
const linkedName = (item) => item?.customer?.customerName || null;
// Struck through as voided: the row itself, or its twin (the API's
// voidedPair covers a twin on another page -- user, 2026-10-22).
const isVoided = (item, voidedNumbers) =>
  item.void === "Y" || !!item.voidedPair || voidedNumbers.has(item.trackingNumber);
// A customer's tab has no W-ID or Location column (user, 2026-10-22).
const CUSTOMER_HIDDEN = ["woId", "location"];
const shipToName = (item) => item?.customerName || linkedName(item);
const statusLabel = (status) =>
  STATUS_OPTIONS.find((o) => o.value === status)?.label || "In-Transit";

/**
 * `customerId` (optional) -- one customer's PER-COMPANY number (the C-n).
 * Given, the page runs as that customer's "Tracking" tab on Customer
 * Details (user, 2026-10-19): every list request, the CSV export and Bulk
 * Update Status are limited to the customer, and what does not fit one
 * customer is left out -- the page chrome, Find (its search is company
 * wide), Add, and the Unlinked tab (a customer's rows are all linked).
 */
export default function UpsTracking({ customerId = null } = {}) {
  const forCustomer = customerId !== null && customerId !== undefined;
  const dispatch = useDispatch();

  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showFindModal, setShowFindModal] = useState(false);
  const [advanceSearchResult, setAdvanceSearchResult] = useState(null);
  const [advanceSearchPage, setAdvanceSearchPage] = useState(1);

  // 20 a page by default (user, 2026-10-27).
  const [pageLimit, setPageLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [exporting, setExporting] = useState(false);
  // Bulk Update Status / Details (user, 2026-10-19)
  const [lastUpdated, setLastUpdated] = useState(null);
  const [detailsFor, setDetailsFor] = useState(null);

  // Manual entry (round 2): null = closed, {} = Add, a row = Edit.
  const [manualForm, setManualForm] = useState(null);

  // The row being emailed (user, 2026-10-26); null = closed.
  const [emailFor, setEmailFor] = useState(null);

  // Bulk Update (user, 2026-10-26): the ticked rows' ids, kept across pages
  // until the filters change or the update is done. Shipments page only.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  // Update Customer for one row with none (user, 2026-10-29); its id.
  const [customerFor, setCustomerFor] = useState(null);
  // Every row seen, by id, so the ticked rows of other pages can still be
  // emailed together (Bulk Email, user 2026-10-27).
  const seenRows = useRef(new Map());
  // Bulk Email: { items, slips } taken when it opens; null = closed.
  const [bulkEmail, setBulkEmail] = useState(null);
  // The row last clicked, shown in light primary (user, 2026-10-27).
  const [activeRowId, setActiveRowId] = useState(null);

  // Round 3 filters, sent to the API with every list request.
  const [statusFilter, setStatusFilter] = useState("all");
  const [courierFilter, setCourierFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [couriers, setCouriers] = useState([]);
  const [locations, setLocations] = useState([]);

  const [dateFilter, setDateFilter] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const pad = (n) => String(n).padStart(2, "0");
  const startOfDay = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 00:00:00`;
  const endOfDay = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} 23:59:59`;

  const getDateRangeForFilter = () => {
    const now = new Date();
    if (dateFilter === "today") {
      return { startDate: startOfDay(now), endDate: endOfDay(now) };
    }
    if (dateFilter === "week") {
      const day = now.getDay();
      const diffToMonday = day === 0 ? 6 : day - 1;
      const monday = new Date(now);
      monday.setDate(now.getDate() - diffToMonday);
      return { startDate: startOfDay(monday), endDate: endOfDay(now) };
    }
    if (dateFilter === "month") {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: startOfDay(first), endDate: endOfDay(now) };
    }
    if (dateFilter === "custom") {
      return {
        startDate: customStart ? `${customStart} 00:00:00` : undefined,
        endDate: customEnd ? `${customEnd} 23:59:59` : undefined,
      };
    }
    return {};
  };

  const listFilterBody = () => ({
    status: statusFilter === "all" ? undefined : statusFilter,
    courierId: courierFilter || undefined,
    locationId: locationFilter || undefined,
    customerId: forCustomer ? customerId : undefined,
  });

  const getUpsTrackings = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
            ? query
            : searchTerm;
        const usedPage = page ?? pageNumber;
        const { startDate, endDate } = getDateRangeForFilter();

        const { data } = await API.post(
          "/ups-trackings?page=" + usedPage + "&limit=" + pageLimit,
          { query: usedQuery, startDate, endDate, ...listFilterBody() },
          { headers: authHeader() },
        );

        if (data?.success) {
          setTrackingData(data?.data || []);
          setLastUpdated(data?.lastUpdated || null);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            }),
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      pageNumber,
      pageLimit,
      dispatch,
      searchTerm,
      dateFilter,
      customStart,
      customEnd,
      statusFilter,
      courierFilter,
      locationFilter,
      customerId,
    ],
  );

  useEffect(() => {
    let timeout = setTimeout(() => {
      getUpsTrackings();
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageNumber,
    pageLimit,
    dateFilter,
    customStart,
    customEnd,
    statusFilter,
    courierFilter,
    locationFilter,
    // Customer Details' previous/next arrows change the customer in place.
    customerId,
  ]);

  // A new filter is a new list: what was ticked in the old one is dropped.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [
    dateFilter,
    customStart,
    customEnd,
    statusFilter,
    courierFilter,
    locationFilter,
    advanceSearchResult,
  ]);

  // The filter dropdowns' options, once.
  useEffect(() => {
    API.get("/courier-services", { headers: authHeader() })
      .then(({ data }) => setCouriers(Array.isArray(data) ? data : []))
      .catch(() => {});
    API.get("/locations", { headers: authHeader() })
      .then(({ data }) => setLocations(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Per row now: the row's courier URL + tracking number, else the UPS link
  // this page always used (see courier.jsx).
  const buildTrackingUrl = (item) => trackingUrlFor(item);

  const copyToClipboard = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(setNotification({ message: successMessage, type: "success" }));
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  };

  /**
   * The shipment as a plain-text block, ready to paste into an email or SMS:
   * the tracking URL first, a blank line, then the shipment (user,
   * 2026-10-27; it used to end with the link).
   *
   * Field order matches the slip staff already send by hand. Date and time are
   * separate lines from the one `shipDate` datetime. City/state/zip are space
   * separated, not comma separated, matching that same slip.
   */
  const buildCopyText = (item) => {
    const shipDate = item.shipDate ? new Date(item.shipDate) : null;
    const datePart = shipDate ? shipDate.toLocaleDateString("en-US") : "";
    const timePart = shipDate && !isDateOnlyRow(item)
      ? shipDate
          .toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          // "02:12 PM" -> "02.12 PM", as on the slip.
          .replace(":", ".")
      : "";

    const cityLine = [item.city, item.state, item.zip]
      .filter(Boolean)
      .join(" ");
    // The service only: the shipping cost is not for the customer (user,
    // 2026-10-26).
    const serviceLine = item.service || "";

    const lines = [
      datePart,
      timePart,
      item.trackingNumber,
      item.void,
      shipToName(item),
      item.contactName,
      [item.address1, item.address2].filter(Boolean).join(" "),
      cityLine,
      serviceLine,
    ].filter((l) => l !== null && l !== undefined && String(l).trim() !== "");

    // No sign-off: this copies the shipment slip and the tracking link only.
    // A fixed signature meant every user pasted Usman's name and number
    // regardless of who was logged in, so whoever sends it now adds their own.
    return [buildTrackingUrl(item), lines.join("\n")].join("\n\n");
  };

  const deleteManualRow = async (item) => {
    if (!window.confirm(`Delete shipment ${item.trackingNumber}?`)) return;
    try {
      const { data } = await API.post(
        "/ups-tracking/delete",
        { id: item.id },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      dispatch(setNotification({ message: "Shipment deleted", type: "success" }));
      getUpsTrackings();
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
  };

  // After an email: an advance search result is a fixed list, so the stamp
  // is put on its rows by hand; the normal list is fetched again.
  const markEmailed = (stamp) => {
    if (advanceSearchResult && stamp) {
      const sent = new Set(stamp.trackingNumbers || [stamp.trackingNumber]);
      const { trackingNumbers, trackingNumber, ...fields } = stamp;
      setAdvanceSearchResult((rows) =>
        rows.map((r) => (sent.has(r.trackingNumber) ? { ...r, ...fields } : r)),
      );
    }
    getUpsTrackings();
  };

  // Bulk Update Customer starts its search with the company name when every
  // ticked row has the same one (user, 2026-10-30).
  const bulkCompanyName = (() => {
    const names = new Set(
      [...selectedIds].map((id) => (seenRows.current.get(id)?.customerName || "").trim()),
    );
    return names.size === 1 ? [...names][0] : "";
  })();

  // Bulk marks on the ticked rows, nothing sent (user, 2026-10-29 / 30):
  // Mark Emailed / Not Emailed, Mark Linked / Unlinked. Each goes on every row
  // with the same tracking number, as the API does.
  const MARKS = {
    emailed: {
      url: "/ups-tracking/mark-emailed",
      body: { emailed: true },
      ask: "as emailed? No email is sent.",
      patch: (r) =>
        r.emailedAt
          ? r
          : { ...r, emailedAt: new Date().toISOString(), emailedTo: "Marked emailed (no email sent)" },
    },
    notEmailed: {
      url: "/ups-tracking/mark-emailed",
      body: { emailed: false },
      ask: "as NOT emailed?",
      patch: (r) => ({ ...r, emailedAt: null, emailedTo: null }),
    },
    linked: {
      url: "/ups-tracking/mark-linked",
      body: { linked: true },
      ask: "as linked? They leave the Unlinked tab without a customer.",
      patch: (r) => ({ ...r, linkMarkedAt: new Date().toISOString() }),
    },
    unlinked: {
      url: "/ups-tracking/mark-linked",
      body: { linked: false },
      ask: "as unlinked? Their customer link is removed.",
      patch: (r) => ({ ...r, linkMarkedAt: null, customer: null, customerId: null }),
    },
  };

  const markSelected = async (kind) => {
    const mark = MARKS[kind];
    const ids = [...selectedIds];
    if (
      !ids.length ||
      !window.confirm(`Mark ${ids.length} shipment${ids.length === 1 ? "" : "s"} ${mark.ask}`)
    )
      return;
    try {
      const { data } = await API.post(
        mark.url,
        { ids, ...mark.body },
        { headers: authHeader() },
      );
      setSelectedIds(new Set());
      dispatch(setNotification({ message: data?.message || "Marked", type: "success" }));
      if (advanceSearchResult) {
        const done = new Set(
          ids.map((id) => seenRows.current.get(id)?.trackingNumber).filter(Boolean),
        );
        setAdvanceSearchResult((rows) =>
          rows.map((r) =>
            ids.includes(r.id) || done.has(r.trackingNumber) ? mark.patch(r) : r,
          ),
        );
      }
      getUpsTrackings();
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    }
  };

  const copyDetailsWithLink = (item) =>
    copyToClipboard(buildCopyText(item), "Shipment details + link copied");

  const formatMoney = (val) =>
    val !== null && val !== undefined ? `$${parseFloat(val).toFixed(2)}` : "-";

  // A manual row's form asks for a day, not a time (it is stored as noon
  // Eastern), so a time shown for it would be made up.
  const formatDate = (val, dateOnly = false) => {
    if (!val) return "-";
    const date = new Date(val);
    if (dateOnly) return date.toLocaleDateString();
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setDateFilter("all");
    setStatusFilter("all");
    setCourierFilter("");
    setLocationFilter("");
    setCustomStart("");
    setCustomEnd("");
    setAdvanceSearchResult(null);
    setAdvanceSearchPage(1);
    setPageNumber(1);
    getUpsTrackings({ clearSearch: true, page: 1 });
  };

  const applyAdvanceSearchResult = (rows) => {
    setAdvanceSearchResult(rows);
    setAdvanceSearchPage(1);
  };

  const csvCell = (val) => {
    const str = val === null || val === undefined ? "" : String(val);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const exportToCsv = async () => {
    try {
      setExporting(true);
      let rows = advanceSearchResult;
      if (!rows) {
        const { startDate, endDate } = getDateRangeForFilter();
        const { data } = await API.post(
          "/ups-trackings?page=1&limit=100000",
          { query: searchTerm, startDate, endDate, ...listFilterBody() },
          { headers: authHeader() },
        );
        rows = data?.success ? data?.data || [] : [];
      }

      if (rows.length === 0) {
        dispatch(
          setNotification({ message: "No data to export", type: "error" }),
        );
        return;
      }

      // Tracking Details left the table but stays in the export.
      const headers = table_head
        .filter(
          (h) =>
            h.value !== "print" && h.value !== "actions" && h.value !== "select",
        )
        .flatMap((h) =>
          h.value === "status"
            ? [h, { name: "Tracking Details", value: "trackingInfo" }]
            : [h],
        );
      const lines = [
        headers.map((h) => csvCell(h.name)).join(","),
        ...rows.map((item) =>
          headers
            .map((h) => {
              if (h.value === "shipDate")
                return csvCell(formatDate(item.shipDate, isDateOnlyRow(item)));
              if (h.value === "courier") return csvCell(item.courier?.name);
              if (h.value === "woId")
                return csvCell(item.woId ? `W-${item.woId}` : "");
              if (h.value === "status")
                return csvCell(
                  isVoided(item, voidedTrackingNumbers)
                    ? "Voided"
                    : statusLabel(item.status),
                );
              if (h.value === "customerName")
                return csvCell(item.customer ? customerLabel(item.customer) : "");
              if (h.value === "location")
                return csvCell(item.location?.locationName);
              if (h.value === "companyName") return csvCell(item.customerName);
              if (h.value === "shippingCost")
                return csvCell(formatMoney(item.shippingCost));
              if (h.value === "emailed")
                return csvCell(item.emailedAt ? "Yes" : "No");
              if (h.value === "address1")
                return csvCell(
                  [item.address1, item.address2].filter(Boolean).join(", "),
                );
              return csvCell(item[h.value]);
            })
            .join(","),
        ),
      ];

      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tracking-info-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setExporting(false);
    }
  };

  // Fed the WHOLE result set rather than the visible page, so a pair split
  // across a page boundary still matches. See voidedTrackingNumbers.js for why
  // the strike follows the tracking number instead of the row's own flag.
  const voidedTrackingNumbers = useMemo(
    () => collectVoidedTrackingNumbers(advanceSearchResult || trackingData),
    [advanceSearchResult, trackingData],
  );

  // The rows on screen, for the select-all box.
  const visibleRows = advanceSearchResult
    ? advanceSearchResult.slice(
        (advanceSearchPage - 1) * pageLimit,
        advanceSearchPage * pageLimit,
      )
    : trackingData;
  visibleRows.forEach((r) => seenRows.current.set(r.id, r));
  // A voided shipment -- the void entry and the original it voids -- cannot
  // be ticked on the All tab (user, 2026-10-29); on the other tabs it can
  // (user, 2026-11-03).
  const lockedVoided = (r) => statusFilter === "all" && isVoided(r, voidedTrackingNumbers);
  const selectableRows = visibleRows.filter((r) => !lockedVoided(r));
  const visibleSelected = selectableRows.filter((r) => selectedIds.has(r.id)).length;
  const allVisibleSelected =
    selectableRows.length > 0 && visibleSelected === selectableRows.length;

  const toggleRow = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // Ticks every row on screen, or unticks them all when they already are.
  const toggleVisible = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      selectableRows.forEach((r) =>
        allVisibleSelected ? next.delete(r.id) : next.add(r.id),
      );
      return next;
    });

  const table_head = [
    // Select box first (user, 2026-10-26), Shipments page only.
    ...(forCustomer
      ? []
      : [
          {
            name: (
              <input
                type="checkbox"
                className="form-check-input"
                aria-label="Select all on this page"
                checked={allVisibleSelected}
                ref={(el) => {
                  if (el) el.indeterminate = visibleSelected > 0 && !allVisibleSelected;
                }}
                onChange={toggleVisible}
              />
            ),
            value: "select",
            centerAlign: true,
          },
        ]),
    // Ship Date and Tracking # read left-aligned (user, 2026-10-19).
    { name: "Ship Date", value: "shipDate" },
    // Second, after Ship Date (Tracking Info, 2026-10-15).
    { name: "Courier", value: "courier" },
    { name: "Tracking #", value: "trackingNumber" },
    // Whether the tracking number was emailed (user, 2026-10-27).
    { name: "Emailed", value: "emailed", centerAlign: true },
    // Linked to a customer, or marked linked by hand (user, 2026-10-30).
    { name: "Linked", value: "linked", centerAlign: true },
    { name: "Void", value: "void", centerAlign: true },
    { name: "Print", value: "print", centerAlign: true },
    { name: "Status", value: "status", centerAlign: true },
    // Order from W-ID on, and no Tracking Details column (user, 2026-10-21;
    // the details are in the Details modal and the CSV). W-ID = the work order
    // WorldShip column E named; WO Customer = the ERP link; Company Name =
    // the name the tracker sent.
    // WO Customer before W-ID (user, 2026-10-22).
    { name: "WO Customer", value: "customerName" },
    { name: "W-ID", value: "woId", centerAlign: true },
    { name: "Location", value: "location" },
    { name: "Company Name", value: "companyName" },
    { name: "Contact", value: "contactName" },
    { name: "Address", value: "address1" },
    { name: "City", value: "city" },
    { name: "State", value: "state" },
    { name: "Zip", value: "zip" },
    // The `service` column; DHL called it shipment type (round 3).
    { name: "Shipment Type", value: "service" },
    { name: "Packages", value: "packages" },
    { name: "Weight (lbs)", value: "weight" },
    { name: "Cost", value: "shippingCost" },
    // Edit on every row (UPS-tracker rows: customer/location/status/details
    // only); delete on rows typed in by hand only.
    { name: "Actions", value: "actions", centerAlign: true },
  ].filter((h) => !(forCustomer && CUSTOMER_HIDDEN.includes(h.value)));

  const table_body = (item, index) => (
    <tr
      key={item.id}
      // A voided shipment is struck through in red so it reads as cancelled at
      // a glance. The class is on the ROW, but the rule below targets the
      // cells: a line-through on <tr> is not painted by the table layout, and
      // the row's own colour would not reach text inside <td> anyway.
      //
      // Matched on the tracking number so the un-flagged twin of a voided
      // shipment is struck too -- see voidedTrackingNumbers above.
      //
      // Row colours (user, 2026-10-27): green once emailed, light grey not
      // yet; a hover colour; the row last clicked in light primary.
      className={[
        "ups-row",
        item.emailedAt ? "ups-row-sent" : "ups-row-unsent",
        activeRowId === item.id ? "ups-row-active" : "",
        isVoided(item, voidedTrackingNumbers) ? "ups-voided" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => setActiveRowId(item.id)}
      // Double-click opens Edit, as the pencil does (user, 2026-10-22).
      onDoubleClick={forCustomer ? undefined : () => setManualForm(item)}
    >
      {!forCustomer && (
        <td
          className="align-middle py-1 text-center"
          // A quick double tick must not open Edit.
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="form-check-input"
            aria-label={`Select ${item.trackingNumber}`}
            disabled={lockedVoided(item)}
            title={
              lockedVoided(item)
                ? "Voided shipments cannot be selected on the All tab"
                : undefined
            }
            checked={selectedIds.has(item.id) && !lockedVoided(item)}
            onChange={() => toggleRow(item.id)}
          />
        </td>
      )}
      <td className="align-middle py-1 text-nowrap">
        {formatDate(item.shipDate, isDateOnlyRow(item))}
      </td>
      <td className="align-middle py-1 text-nowrap">
        <CourierCell courier={item.courier} />
      </td>
      <td className="align-middle py-1 text-nowrap">
        {/* Number on the left, its three buttons pushed to the right edge so
            they line up down the column whatever the number's length. */}
        <div className="d-flex align-items-center gap-2">
          {/* The number IS the link to the courier's tracking page (user,
              2026-10-21 -- the separate open button is gone). A courier with
              no tracking URL falls back to the UPS link, as before. */}
          <a
            href={buildTrackingUrl(item)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.trackingNumber}
          </a>
          {/* No copy-URL button any more (user, 2026-10-27): the slip below
              starts with the link. */}
          <button
            type="button"
            className="btn btn-link p-0 border-0 lh-1 ms-auto"
            title="Copy tracking link + shipment details"
            onClick={() => copyDetailsWithLink(item)}
          >
            <i className="mdi mdi-content-paste"></i>
          </button>
          {/* Email the shipment to the customer (user, 2026-10-26). Green
              once its tracking number has been emailed. */}
          <button
            type="button"
            className={`btn btn-link p-0 border-0 lh-1${
              item.emailedAt ? " text-success" : ""
            }`}
            aria-label={`Email ${item.trackingNumber}`}
            title={
              item.emailedAt
                ? `Emailed to ${item.emailedTo || "-"} on ${new Date(
                    item.emailedAt,
                  ).toLocaleString()}`
                : "Email shipment to customer"
            }
            onClick={() => setEmailFor(item)}
          >
            <i
              className={`mdi ${
                item.emailedAt ? "mdi-email-check-outline" : "mdi-email-outline"
              }`}
            ></i>
          </button>
        </div>
      </td>
      <td className="align-middle py-1 text-nowrap text-center">
        {item.emailedAt ? (
          <span
            className="badge bg-success"
            title={`Emailed to ${item.emailedTo || "-"} on ${new Date(
              item.emailedAt,
            ).toLocaleString()}`}
          >
            Yes
          </span>
        ) : (
          <span className="badge bg-secondary">No</span>
        )}
      </td>
      <td className="align-middle py-1 text-nowrap text-center">
        {item.customer ? (
          <span className="badge bg-success">Yes</span>
        ) : item.linkMarkedAt ? (
          <span
            className="badge bg-info text-dark"
            title={`Marked linked on ${new Date(item.linkMarkedAt).toLocaleString()}`}
          >
            Marked
          </span>
        ) : (
          <span className="badge bg-secondary">No</span>
        )}
      </td>
      <td className="align-middle py-1 text-nowrap text-center">
        {item.void === "Y" ? (
          <span className="badge bg-danger">Voided</span>
        ) : (
          item.void || "-"
        )}
      </td>
      <td className="align-middle py-1 text-nowrap text-center">
        <ZplAddressLabelButton
          title="Print address label"
          lines={[
            shipToName(item),
            item.contactName,
            [item.address1, item.address2].filter(Boolean).join(", "),
            [item.city, item.state, item.zip].filter(Boolean).join(", "),
          ]}
        />
      </td>
      <td className="align-middle py-1 text-nowrap text-center">
        {/* A voided shipment -- either twin -- reads Voided, whatever its
            stored status (user, 2026-10-22). */}
        {isVoided(item, voidedTrackingNumbers) ? (
          <span className="badge bg-danger">Voided</span>
        ) : (
          <span
            className={`badge ${
              item.status === "delivered" ? "bg-success" : "bg-warning text-dark"
            }`}
          >
            {statusLabel(item.status)}
          </span>
        )}
      </td>
      <td
        className="align-middle py-1 text-nowrap text-truncate"
        style={{ maxWidth: 200 }}
        title={linkedName(item) || ""}
      >
        {/* The linked customer (W-ID or set by hand), by name only (user,
            2026-10-22), else nothing. */}
        {item.customer ? (
          <span className="d-inline-flex align-items-center gap-1">
            <Link to={`/customer/details/${item.customer.customerId}`}>
              {linkedName(item) || customerLabel(item.customer)}
            </Link>
            {/* Change a linked customer too (user, 2026-10-30). */}
            {!forCustomer && (
              <button
                type="button"
                className="btn btn-sm btn-link py-0 px-1"
                title="Change customer"
                aria-label={`Change customer of ${item.trackingNumber}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCustomerFor(item.id);
                }}
                onDoubleClick={(e) => e.stopPropagation()}
              >
                <i className="mdi mdi-pencil-outline"></i>
              </button>
            )}
          </span>
        ) : forCustomer ? (
          "-"
        ) : (
          // No customer: link this one row (user, 2026-10-29), as Update
          // Customer does for the ticked rows.
          <button
            type="button"
            className="btn btn-sm btn-outline-primary py-0 px-2"
            aria-label={`Update customer of ${item.trackingNumber}`}
            onClick={(e) => {
              e.stopPropagation();
              setCustomerFor(item.id);
            }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <i className="mdi mdi-account-plus-outline me-1"></i>
            Update
          </button>
        )}
      </td>
      {!forCustomer && (
        <td className="align-middle py-1 text-nowrap text-center">
          {item.woId ? (
            <Link to={`/work-order/details/all/${item.woId}?page=1`}>
              W-{item.woId}
            </Link>
          ) : (
            "-"
          )}
        </td>
      )}
      {!forCustomer && (
        <td className="align-middle py-1 text-nowrap">
          {item.location?.locationName || "-"}
        </td>
      )}
      <td
        className="align-middle py-1 text-nowrap text-truncate"
        style={{ maxWidth: 200 }}
        title={item.customerName || ""}
      >
        {item.customerName || "-"}
      </td>
      <td
        className="align-middle py-1 text-nowrap text-truncate"
        style={{ maxWidth: 140 }}
        title={item.contactName || ""}
      >
        {item.contactName || "-"}
      </td>
      <td
        className="align-middle py-1 text-nowrap text-truncate"
        style={{ maxWidth: 180 }}
        title={[item.address1, item.address2].filter(Boolean).join(", ")}
      >
        {[item.address1, item.address2].filter(Boolean).join(", ") || "-"}
      </td>
      <td
        className="align-middle py-1 text-nowrap text-truncate"
        style={{ maxWidth: 110 }}
        title={item.city || ""}
      >
        {item.city || "-"}
      </td>
      <td className="align-middle py-1 text-nowrap">{item.state || "-"}</td>
      <td className="align-middle py-1 text-nowrap">{item.zip || "-"}</td>
      <td className="align-middle py-1 text-nowrap">{item.service || "-"}</td>
      <td className="align-middle py-1 text-nowrap">{item.packages ?? "-"}</td>
      <td className="align-middle py-1 text-nowrap">{item.weight ?? "-"}</td>
      <td className="align-middle py-1 text-nowrap">
        {formatMoney(item.shippingCost)}
      </td>
      <td className="align-middle py-1 text-nowrap text-center">
        <div className="d-flex justify-content-center gap-2">
          {/* The courier's full tracking history -- DHL or UPS. Greyed out
              when the courier has no tracking API to ask. Hidden on the
              Shipments page (user, 2026-10-22); the customer's tab keeps it,
              it is that tab's only action. */}
          {(forCustomer || SHOW_DETAILS_ON_PAGE) && (
            <button
              type="button"
              className="btn btn-link p-0 border-0 lh-1 text-info"
              aria-label={`Details ${item.trackingNumber}`}
              title={
                hasTrackingApi(item.courier)
                  ? "Tracking details"
                  : "No tracking API for this courier (Settings -> Couriers)"
              }
              disabled={!hasTrackingApi(item.courier) || !item.trackingNumber}
              onClick={() => setDetailsFor(item)}
            >
              <i className="mdi mdi-information-outline"></i>
            </button>
          )}
          {/* A customer's tab is view-only: Details, no Edit or Delete
              (user, 2026-10-22). */}
          {!forCustomer && (
            <button
              type="button"
              className="btn btn-link p-0 border-0 lh-1"
              aria-label={`Edit ${item.trackingNumber}`}
              title="Edit"
              onClick={() => setManualForm(item)}
            >
              <i className="mdi mdi-pencil"></i>
            </button>
          )}
          {!forCustomer && isDeletableRow(item) && (
            <button
              type="button"
              className="btn btn-link p-0 border-0 lh-1 text-danger"
              aria-label={`Delete ${item.trackingNumber}`}
              title="Delete"
              onClick={() => deleteManualRow(item)}
            >
              <i className="mdi mdi-delete"></i>
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div
      className={forCustomer ? "w-100 pt-2" : ""}
      style={forCustomer ? undefined : { marginLeft: 260, height: "100vh" }}
    >
      <style>
        {`
          /* Row colours (user, 2026-10-27), on the cells so the table's own
             cell background does not cover them: emailed green, not yet
             light grey, hover and the clicked row in primary tints. */
          .ups-row-sent > td { background-color: #e3f4ea !important; }
          .ups-row-unsent > td { background-color: #f3f4f6 !important; }
          .ups-row:hover > td { background-color: #e7ecfb !important; }
          .ups-row-active > td,
          .ups-row-active:hover > td {
            background-color: #cfdafa !important;
          }
          /* Voided shipments read as cancelled at a glance.
             Targets the CELLS, not the row: line-through on a <tr> is not
             painted by the table layout, and colour set on <tr> does not
             cascade to text inside <td>. */
          .ups-voided > td {
            text-decoration: line-through;
            text-decoration-color: #dc3545;
            text-decoration-thickness: 2px;
            color: #b02a37;
          }
          /* The "Voided" badge states plainly why the row is struck, so it is
             exempt -- a struck-out label reading Voided is nonsense. Exempting
             the BADGE rather than the whole Void cell, because the twin row
             has no badge, only a plain "N", and leaving that one glyph
             upright in an otherwise struck row looked like a rendering fault. */
          .ups-voided td .badge {
            text-decoration: none;
            color: #fff;
          }
          /* Buttons keep their own colour and are never struck through --
             they are still live controls on a voided row. */
          .ups-voided td button,
          .ups-voided td a {
            text-decoration: none;
          }
        `}
      </style>
      {loading && <Spinner />}

      <div className="d-flex px-2 justify-content-between mb-3 align-items-center mx-2 pt-2">
        <div className="d-flex align-items-center gap-2">
          {forCustomer ? (
            <h4 className="mb-0">Shipments</h4>
          ) : (
            <h2 className="mb-0">Shipments</h2>
          )}
          {advanceSearchResult && (
            <span className="text-muted text-nowrap">
              Showing {advanceSearchResult.length} advance search result
              {advanceSearchResult.length === 1 ? "" : "s"}
            </span>
          )}
        </div>

        <div className="d-flex flex-grow-1 justify-content-end align-items-center gap-2">
          {/* The one Refresh -- the green "Refresh" button is gone (user,
              2026-10-22). A customer's tab has no Refresh, date filter,
              location filter or Bulk Update. */}
          {!forCustomer && (
            <RefreshButton
              className="btn btn-outline-primary"
              loading={loading}
              onClick={() => getUpsTrackings()}
            />
          )}
          <div className="btn-group">
            <button
              className="btn btn-outline-primary"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              {[5, 10, 15, 20, 50, 100].map((limit) => (
                <li key={limit} onClick={() => setPageLimit(limit)}>
                  <button className="dropdown-item">{limit}</button>
                </li>
              ))}
            </ul>
          </div>

          {!forCustomer && (
            <div className="btn-group">
              <button
                className="btn btn-outline-primary"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {
                  {
                    all: "All Dates",
                    today: "Today",
                    week: "This Week",
                    month: "This Month",
                    custom: "Custom Range",
                  }[dateFilter]
                }
              </button>
              <ul className="dropdown-menu">
                {[
                  { value: "all", label: "All Dates" },
                  { value: "today", label: "Today" },
                  { value: "week", label: "This Week" },
                  { value: "month", label: "This Month" },
                  { value: "custom", label: "Custom Range" },
                ].map((opt) => (
                  <li
                    key={opt.value}
                    onClick={() => {
                      setDateFilter(opt.value);
                      setPageNumber(1);
                    }}
                  >
                    <button className="dropdown-item">{opt.label}</button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!forCustomer && dateFilter === "custom" && (
            <>
              <input
                type="date"
                className="form-control"
                style={{ width: 150 }}
                value={customStart}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  setPageNumber(1);
                }}
              />
              <span>to</span>
              <input
                type="date"
                className="form-control"
                style={{ width: 150 }}
                value={customEnd}
                onChange={(e) => {
                  setCustomEnd(e.target.value);
                  setPageNumber(1);
                }}
              />
            </>
          )}

          {!forCustomer && (
            <>
              <BulkUpdateStatus
                lastUpdated={lastUpdated}
                onDone={() => getUpsTrackings()}
              />

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowFindModal(true)}
              >
                Find
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setManualForm({})}
              >
                <i className="mdi mdi-plus"></i> Add
              </button>
            </>
          )}

          <button
            type="button"
            className="btn btn-outline-success"
            onClick={exportToCsv}
            disabled={exporting}
          >
            {exporting ? "Exporting..." : "Export CSV"}
          </button>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getUpsTrackings({ query: searchTerm, page: 1 });
              } else {
                getUpsTrackings({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search tracking #, customer, contact..."
              value={searchTerm}
              className="form-control"
              style={{ width: 260 }}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            <button
              className="btn btn-outline-danger"
              type="button"
              onClick={clearAllFilters}
            >
              Clear
            </button>
          </form>
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 px-2 mx-2 mb-2 flex-wrap">
        <div className="d-flex" role="tablist" aria-label="Status filter">
          {(forCustomer ? CUSTOMER_STATUS_TABS : STATUS_TABS).map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={statusFilter === t.value}
              // On a customer's tab an inactive tab reads like the Customer
              // Details tabs above it: primary text on the light grey of
              // that tab strip, so it stands out from the white card (user,
              // 2026-10-22).
              className={`btn ${
                statusFilter === t.value
                  ? "btn-primary"
                  : forCustomer
                    ? "text-primary bg-light"
                    : "bg-white"
              }`}
              onClick={() => {
                setStatusFilter(t.value);
                setPageNumber(1);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <select
          aria-label="Courier filter"
          className="form-select"
          style={{ width: 180 }}
          value={courierFilter}
          onChange={(e) => {
            setCourierFilter(e.target.value);
            setPageNumber(1);
          }}
        >
          <option value="">All Couriers</option>
          {couriers.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
        {!forCustomer && (
          <select
            aria-label="Location filter"
            className="form-select"
            style={{ width: 200 }}
            value={locationFilter}
            onChange={(e) => {
              setLocationFilter(e.target.value);
              setPageNumber(1);
            }}
          >
            <option value="">All Locations</option>
            {/* As on W-Orders (user, 2026-10-22). */}
            <option value="no-location">No Location</option>
            {locations.map((l) => (
              <option key={l.id} value={String(l.id)}>
                {l.locationName}
              </option>
            ))}
          </select>
        )}
        {/* Bulk Update (user, 2026-10-26): the ticked rows, after the
            location filter. Not Bulk Update Status, which asks couriers. */}
        {!forCustomer && (
          <>
            <button
              type="button"
              className="btn btn-primary"
              disabled={selectedIds.size === 0}
              onClick={() => setShowBulkUpdate(true)}
            >
              <i className="mdi mdi-pencil-box-multiple-outline me-1"></i>
              {/* Was "Bulk Update" (renamed, user 2026-10-27). */}
              Update Customer{selectedIds.size ? ` (${selectedIds.size})` : ""}
            </button>
            {/* Bulk Email (user, 2026-10-27): the ticked shipments in one
                email -- one To, one subject, one body with each slip. */}
            <button
              type="button"
              className="btn btn-primary"
              disabled={selectedIds.size === 0}
              onClick={() => {
                const items = [...selectedIds]
                  .map((id) => seenRows.current.get(id))
                  .filter(Boolean);
                if (items.length)
                  setBulkEmail({ items, slips: items.map(buildCopyText) });
              }}
            >
              <i className="mdi mdi-email-multiple-outline me-1"></i>
              Bulk Email{selectedIds.size ? ` (${selectedIds.size})` : ""}
            </button>
            {/* Mark Emailed (user, 2026-10-29): stamped emailed, no email
                sent -- for shipments that need none. */}
            <button
              type="button"
              className="btn btn-success"
              disabled={selectedIds.size === 0}
              onClick={() => markSelected("emailed")}
            >
              <i className="mdi mdi-email-check-outline me-1"></i>
              Mark Emailed{selectedIds.size ? ` (${selectedIds.size})` : ""}
            </button>
            {/* Mark Linked (user, 2026-10-30): out of the Unlinked tab with
                no customer. */}
            <button
              type="button"
              className="btn btn-success"
              disabled={selectedIds.size === 0}
              onClick={() => markSelected("linked")}
            >
              <i className="mdi mdi-link-variant me-1"></i>
              Mark Linked{selectedIds.size ? ` (${selectedIds.size})` : ""}
            </button>
            {/* The way back, on the All tab (user, 2026-10-30). */}
            {statusFilter === "all" && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={selectedIds.size === 0}
                  onClick={() => markSelected("notEmailed")}
                >
                  <i className="mdi mdi-email-remove-outline me-1"></i>
                  Mark Not Emailed{selectedIds.size ? ` (${selectedIds.size})` : ""}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={selectedIds.size === 0}
                  onClick={() => markSelected("unlinked")}
                >
                  <i className="mdi mdi-link-variant-off me-1"></i>
                  Mark Unlinked{selectedIds.size ? ` (${selectedIds.size})` : ""}
                </button>
              </>
            )}
            {selectedIds.size > 0 && (
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => setSelectedIds(new Set())}
              >
                Deselect all
              </button>
            )}
          </>
        )}
      </div>

      <div
        className="px-2 flex-grow-1 overflow-y-auto d-flex"
        style={{ height: "80vh", overflowY: "auto", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={visibleRows}
            tableSize="sm"
            disableSorting={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2 me-2">
        {advanceSearchResult ? (
          <PaginationWithState
            totalPages={Math.ceil(advanceSearchResult.length / pageLimit) || 1}
            currPage={advanceSearchPage}
            setCurrPage={setAdvanceSearchPage}
          />
        ) : (
          <PaginationWithState
            totalPages={totalPages}
            currPage={pageNumber}
            setCurrPage={setPageNumber}
          />
        )}
      </div>

      <TrackingDetailsModal
        item={detailsFor}
        onHide={() => setDetailsFor(null)}
        formatShipDate={(item) => formatDate(item.shipDate, isDateOnlyRow(item))}
        customerLabel={customerLabel}
      />

      <ShipmentEmailModal
        item={emailFor}
        trackingUrl={emailFor ? buildTrackingUrl(emailFor) : ""}
        shipDate={
          emailFor?.shipDate
            ? new Date(emailFor.shipDate).toLocaleDateString("en-US")
            : ""
        }
        locations={locations}
        onHide={() => setEmailFor(null)}
        onSent={(stamp, message) => {
          setEmailFor(null);
          dispatch(
            setNotification({ message: message || "Email sent", type: "success" }),
          );
          markEmailed(stamp);
        }}
      />

      <ShipmentEmailModal
        items={bulkEmail?.items}
        slips={bulkEmail?.slips}
        locations={locations}
        onHide={() => setBulkEmail(null)}
        onSent={(stamp, message) => {
          setBulkEmail(null);
          setSelectedIds(new Set());
          dispatch(
            setNotification({ message: message || "Email sent", type: "success" }),
          );
          markEmailed(stamp);
        }}
      />

      <BulkUpdateFieldsModal
        show={!!customerFor}
        ids={customerFor ? [customerFor] : []}
        search={seenRows.current.get(customerFor)?.customerName || ""}
        onHide={() => setCustomerFor(null)}
        onDone={(message) => {
          setCustomerFor(null);
          dispatch(
            setNotification({ message: message || "Updated", type: "success" }),
          );
          getUpsTrackings();
        }}
      />

      <BulkUpdateFieldsModal
        show={showBulkUpdate}
        ids={[...selectedIds]}
        search={bulkCompanyName}
        onHide={() => setShowBulkUpdate(false)}
        onDone={(message) => {
          setShowBulkUpdate(false);
          setSelectedIds(new Set());
          dispatch(
            setNotification({ message: message || "Updated", type: "success" }),
          );
          getUpsTrackings();
        }}
      />

      <Modal size="lg" show={!!manualForm} onHide={() => setManualForm(null)}>
        <Modal.Body
          className="shadow-lg bg-white px-3"
          style={{ borderRadius: 15 }}
        >
          {manualForm && (
            <ManualEntryForm
              row={manualForm.id ? manualForm : null}
              onCancel={() => setManualForm(null)}
              onSaved={() => {
                const wasEdit = !!manualForm.id;
                setManualForm(null);
                dispatch(
                  setNotification({
                    message: wasEdit ? "Shipment updated" : "Shipment added",
                    type: "success",
                  }),
                );
                getUpsTrackings();
              }}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        size="xl"
        show={showFindModal}
        onHide={() => setShowFindModal(false)}
      >
        <Modal.Body
          className="shadow-lg bg-white px-3"
          style={{ borderRadius: 15 }}
        >
          <UpsAdvanceSearchModal
            showModal={setShowFindModal}
            setSearchResult={applyAdvanceSearchResult}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
